// TypeScript row shapes for the Imported invoices feature (QTG invoice export).
// Mirrors the backend ImportedInvoiceDto / ImportedInvoiceEntryDto contracts.

export type ImportedInvoiceMatchState =
  | "unmatched"
  | "partiallyMatched"
  | "matched"
  | "pending"
  | "paid";

export const IMPORTED_INVOICE_MATCH_STATES: ImportedInvoiceMatchState[] = [
  "unmatched",
  "partiallyMatched",
  "matched",
  "pending",
  "paid",
];

export type ImportedInvoiceEntry = {
  importedInvoiceEntryId: number;
  importedInvoiceId: number;
  position: number;
  vin: string | null;
  premiumAmount: number;
  insuranceLine: string;
  insuranceLineCodeId: number | null;
  insurerCode: string | null;
  insurerName: string | null;
  insurerId: number | null;
  insurerNameResolved: string | null;
  clientCode: string | null;
  clientName: string | null;
  clientId: number | null;
  clientNameResolved: string | null;
  offerId: string | null;
  paymentPlanEntryId: number | null;
  paymentId: number | null;
  // context of the matched installment (not persisted)
  policyNumberPaymentPlanEntry: string | null;
  policyIdPaymentPlanEntry: number | null;
  premiumAmountPaymentPlanEntry: number | null;
  premiumDebtPaymentPlanEntry: number | null;
  isPaidPaymentPlanEntry: boolean | null;
  clientNamePaymentPlanEntry: string | null;
  clientIdPaymentPlanEntry: number | null;
  validFromPaymentPlanEntry: string | null;
  paymentDate: string | null;
  candidateCount: number;
  createdAt: string;
  createdByUserId: string;
  createdByUserName: string;
  createdByUserEmail: string;
  lastModifiedAt: string;
  lastModifiedByUserId: string;
  lastModifiedByUserName: string;
  lastModifiedByUserEmail: string;
  isDeleted: boolean;
  rowVersion: any;
  // UI-hydrated display fields
  validFromPaymentPlanEntryFormatted?: string;
  paymentDateFormatted?: string;
  insurerDisplayName?: string;
  clientDisplayName?: string;
};

export type ImportedInvoice = {
  importedInvoiceId: number;
  source: string;
  externalId: string;
  invoiceNumber: string;
  issuedDate: string;
  dueDate: string;
  paidAtSource: string | null;
  payerCode: string | null;
  payerName: string | null;
  payerClientId: number | null;
  payerClientInfo: { id: number; name: string; internalNumber: number; isCompany: boolean } | null;
  premiumAmount: number;
  currencyCode: number;
  grecoCompanyNumber: number;
  isGroupInvoice: boolean;
  pendingBankStatementEntryId: number | null;
  pendingBankStatementId: number | null;
  pendingBankStatementEntryBookingDate: string | null;
  pendingBankStatementEntryAmount: number | null;
  pendingBankStatementEntryClientName: string | null;
  pendingBankStatementEntryFormatted?: string; // "booking date, amount, payer" of the pending entry
  importedAt: string;
  importedByUserId: string;
  importFileName: string | null;
  importDocumentPath: string | null;
  comment: string | null;
  matchState: ImportedInvoiceMatchState;
  entryCount: number;
  matchedEntryCount: number;
  paidEntryCount: number;
  createdAt: string;
  createdByUserId: string;
  createdByUserName: string;
  createdByUserEmail: string;
  lastModifiedAt: string;
  lastModifiedByUserId: string;
  lastModifiedByUserName: string;
  lastModifiedByUserEmail: string;
  isDeleted: boolean;
  rowVersion: any;
  importedInvoiceEntries: ImportedInvoiceEntry[];
  // UI-hydrated fields
  issuedDateFormatted?: string;
  dueDateFormatted?: string;
  paidAtSourceFormatted?: string;
  importedAtFormatted?: string;
  payerDisplayName?: string;
  matchStateLabel?: string;
  unmatchedEntryCount?: number;
};

export type ImportedInvoiceImportResult = {
  importedCount: number;
  updatedCount: number;
  matchedEntryCount: number;
  unmatchedEntryCount: number;
  pulledIssuedFrom?: string | null; // pull only: window asked from QTG
  pulledIssuedTo?: string | null;
  pulledRowCount?: number; // pull only: rows the feed answered with
  unresolvedPayerCodes: string[];
  unresolvedClientCodes: string[];
  unresolvedInsurerCodes: string[];
  warnings: string[];
  importedInvoices: ImportedInvoice[];
};

export type ImportedInvoiceMetadata = {
  selected: boolean;
  visible: boolean;
  deleted: boolean;
  // fields required by the shared GrecoReactGrid / cell type handlers
  inEditMode: boolean;
  changes: any[];
  editable: boolean;
  oldRow: any;
};

export type ImportedInvoiceMetadataMap = {
  [importedInvoiceId: number]: ImportedInvoiceMetadata;
};

export type ImportedInvoiceEntryMetadata = {
  selected: boolean;
  visible: boolean;
  matched: boolean;
  highlighted: boolean;
  inEditMode: boolean;
  changes: any[];
  editable: boolean;
  oldRow: any;
};

export type ImportedInvoiceEntryMetadataMap = {
  [importedInvoiceEntryId: number]: ImportedInvoiceEntryMetadata;
};
